/**
 * Wizard Context Provider
 *
 * Provides wizard state and actions to all wizard components.
 * Uses React Context with useReducer for state management.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { SubmissionValidationResult } from '@/types/validation';
import {
  type WizardState,
  type WizardStep,
  type SourceType,
  type FormatType,
  type WizardEmployee,
  type ParseResult,
  type GeneratedFile,
  createInitialState,
  WIZARD_STEPS,
} from './types';
import { wizardReducer, isStepComplete } from './wizardReducer';

// =============================================================================
// CONTEXT TYPE
// =============================================================================

interface WizardContextType {
  // State
  state: WizardState;

  // Step navigation computed values
  currentStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  canGoPrev: boolean;

  // Navigation actions
  goToStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1: Source actions
  setSourceType: (type: SourceType) => void;
  setUploadedFile: (file: File | null) => void;
  setParseResult: (result: ParseResult) => void;

  // Step 2: Period actions
  setPeriod: (month: number, year: number) => void;
  setFormatType: (type: FormatType) => void;

  // Step 3: Employee actions
  setEmployees: (employees: WizardEmployee[]) => void;
  updateEmployee: (id: string, updates: Partial<WizardEmployee>) => void;
  addEmployee: (employee: Omit<WizardEmployee, 'id'>) => void;
  removeEmployee: (id: string) => void;
  setValidationResult: (result: SubmissionValidationResult) => void;
  acknowledgeWarning: (key: string) => void;

  // Step 4: Generate actions
  setGeneratedFile: (file: GeneratedFile) => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

const WizardContext = createContext<WizardContextType | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface WizardProviderProps {
  children: ReactNode;
}

/**
 * Wizard context provider component.
 * Wraps the wizard pages to provide state management.
 */
export function WizardProvider({ children }: WizardProviderProps) {
  const [state, dispatch] = useReducer(wizardReducer, null, createInitialState);

  // ---------------------------------------------------------------------------
  // Computed values
  // ---------------------------------------------------------------------------

  const currentStepIndex = WIZARD_STEPS.indexOf(state.currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1;

  // Check if can proceed to next step
  const canGoNext = useMemo(() => {
    return isStepComplete(state, state.currentStep);
  }, [state]);

  const canGoPrev = !isFirstStep;

  // ---------------------------------------------------------------------------
  // Navigation actions
  // ---------------------------------------------------------------------------

  const goToStep = useCallback((step: WizardStep) => {
    dispatch({ type: 'GO_TO_STEP', payload: step });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  // ---------------------------------------------------------------------------
  // Source actions
  // ---------------------------------------------------------------------------

  const setSourceType = useCallback((type: SourceType) => {
    dispatch({ type: 'SET_SOURCE_TYPE', payload: type });
  }, []);

  const setUploadedFile = useCallback((file: File | null) => {
    if (file) {
      dispatch({
        type: 'SET_UPLOADED_FILE',
        payload: {
          file,
          name: file.name,
          size: file.size,
          uploadedAt: new Date(),
        },
      });
    } else {
      dispatch({ type: 'SET_UPLOADED_FILE', payload: null });
    }
  }, []);

  const setParseResult = useCallback((result: ParseResult) => {
    dispatch({ type: 'SET_PARSE_RESULT', payload: result });
  }, []);

  // ---------------------------------------------------------------------------
  // Period actions
  // ---------------------------------------------------------------------------

  const setPeriod = useCallback((month: number, year: number) => {
    dispatch({ type: 'SET_PERIOD', payload: { month, year } });
  }, []);

  const setFormatType = useCallback((type: FormatType) => {
    dispatch({ type: 'SET_FORMAT_TYPE', payload: type });
  }, []);

  // ---------------------------------------------------------------------------
  // Employee actions
  // ---------------------------------------------------------------------------

  const setEmployees = useCallback((employees: WizardEmployee[]) => {
    dispatch({ type: 'SET_EMPLOYEES', payload: employees });
  }, []);

  const updateEmployee = useCallback(
    (id: string, updates: Partial<WizardEmployee>) => {
      dispatch({ type: 'UPDATE_EMPLOYEE', payload: { id, updates } });
    },
    []
  );

  const addEmployee = useCallback((employee: Omit<WizardEmployee, 'id'>) => {
    dispatch({
      type: 'ADD_EMPLOYEE',
      payload: { ...employee, id: uuidv4() },
    });
  }, []);

  const removeEmployee = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_EMPLOYEE', payload: id });
  }, []);

  const setValidationResult = useCallback(
    (result: SubmissionValidationResult) => {
      dispatch({ type: 'SET_VALIDATION_RESULT', payload: result });
    },
    []
  );

  const acknowledgeWarning = useCallback((key: string) => {
    dispatch({ type: 'ACKNOWLEDGE_WARNING', payload: key });
  }, []);

  // ---------------------------------------------------------------------------
  // Generate actions
  // ---------------------------------------------------------------------------

  const setGeneratedFile = useCallback((file: GeneratedFile) => {
    dispatch({ type: 'SET_GENERATED_FILE', payload: file });
  }, []);

  // ---------------------------------------------------------------------------
  // UI actions
  // ---------------------------------------------------------------------------

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value = useMemo<WizardContextType>(
    () => ({
      state,
      currentStepIndex,
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoPrev,
      goToStep,
      nextStep,
      prevStep,
      setSourceType,
      setUploadedFile,
      setParseResult,
      setPeriod,
      setFormatType,
      setEmployees,
      updateEmployee,
      addEmployee,
      removeEmployee,
      setValidationResult,
      acknowledgeWarning,
      setGeneratedFile,
      setLoading,
      setError,
      reset,
    }),
    [
      state,
      currentStepIndex,
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoPrev,
      goToStep,
      nextStep,
      prevStep,
      setSourceType,
      setUploadedFile,
      setParseResult,
      setPeriod,
      setFormatType,
      setEmployees,
      updateEmployee,
      addEmployee,
      removeEmployee,
      setValidationResult,
      acknowledgeWarning,
      setGeneratedFile,
      setLoading,
      setError,
      reset,
    ]
  );

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access wizard context.
 * Must be used within a WizardProvider.
 */
export function useWizard(): WizardContextType {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
